"use client"

import CustomIcon from "components/CustomIcon"
import Image from "next/image"
import FeedbackIcon from "public/icons/feedback.svg"
import { useState, useRef } from "react"
import BulbIcon from "public/icons/bulb.svg";
import BugIcon from "public/icons/bug.svg"
import { requestFeature } from "api/requestFeature"
import { reportBug } from "api/reportBug"
// import { reportBug } from "api/reportBug"

// Add animation keyframes as a style element
const animationStyles = `
@keyframes slideInFromRight {
  0% {
    opacity: 0;
    transform: translateX(20px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}
`;

export const FeedbackButton = () => {
    const [isOpen, setIsOpen] = useState(false)
    const [showSurvey, setShowSurvey] = useState(false)
    const [feedbackType, setFeedbackType] = useState<'bug' | 'feature' | null>(null)
    const [feedbackText, setFeedbackText] = useState('')
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [attachment, setAttachment] = useState<File | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitSuccess, setSubmitSuccess] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    
    // Function to handle reporting a bug
    const handleReportBug = () => {
        setFeedbackType('bug')
        setShowSurvey(true)
        setIsOpen(false)
    }
    
    // Function to handle feature requests
    const handleFeatureRequest = () => {
        setFeedbackType('feature')
        setShowSurvey(true)
        setIsOpen(false)
    }
    
    const handleSubmitFeedback = async () => {
        // Form validation
        if (!feedbackText.trim()) return;
        if (!name.trim()) {
            setError('Please enter your name');
            return;
        }
        if (!email.trim()) {
            setError('Please enter your email');
            return;
        }
        
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            return;
        }
        
        setError(null);
        setIsSubmitting(true);
        
        try {
            // Create form data to handle file upload
            const formData = new FormData();
            formData.append('email', email);
            formData.append('name', name);
            formData.append('suggestion', feedbackText);
            
            // Add attachment if exists
            if (attachment) {
                formData.append('attachment', attachment);
            }
            
            // Send to the appropriate API based on feedback type
            let response;
            if (feedbackType === 'bug') {
                response = await reportBug(formData);
            } else {
                response = await requestFeature(formData);
            }
            
            setSubmitSuccess(true);
            setIsSubmitting(false);
            
            // Close the survey after showing success message
            setTimeout(() => {
                setShowSurvey(false);
                setSubmitSuccess(false);
                setFeedbackType(null);
                setName('');
                setEmail('');
                setFeedbackText('');
                setAttachment(null);
            }, 3000);
            
        } catch (err) {
            console.error('Error submitting feedback:', err);
            setError('Failed to submit feedback. Please try again later.');
            setIsSubmitting(false);
        }
    }
    const handleCloseSurvey = () => {
        setShowSurvey(false)
        setFeedbackText('')
        setFeedbackType(null)
        setName('')
        setEmail('')
        setAttachment(null)
        setError(null)
    }
    
    // Helper function to get file type label
    const getFileTypeLabel = (mimeType: string) => {
        if (mimeType.startsWith('image/')) return 'Image';
        if (mimeType === 'application/pdf') return 'PDF';
        if (mimeType === 'video/mp4') return 'Video';
        return 'File';
    }
    
    return (
        <div className="fixed bottom-5 right-5 z-50 invisible lg:visible">
            <style dangerouslySetInnerHTML={{ __html: animationStyles }} />
            {/* Floating options */}
            <div className={`absolute bottom-4 left-auto right-20 transition-all duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none translate-x-4'}`}>
                <div className="flex flex-col gap-3">
                    <button 
                        className="bg-white text-blue-600 rounded-full shadow-xl px-6 py-4 text-lg font-medium flex items-center gap-4 hover:bg-blue-50 transform hover:scale-105 transition-all duration-200 w-52"
                        onClick={handleReportBug}
                        style={{
                            animation: isOpen ? 'slideInFromRight 0.3s ease-out forwards' : 'none'
                        }}
                    >
                        <div className="p-2 bg-red-100 rounded-full">
                            <BugIcon className="w-6 h-6 text-red-500" />
                        </div>
                        Report Bug
                    </button>
                    
                    <button 
                        id="request-new-feature-button"
                        className="bg-white text-blue-600 rounded-full shadow-xl px-6 py-4 text-lg font-medium flex items-center gap-4 hover:bg-blue-50 transform hover:scale-105 transition-all duration-200 w-64 whitespace-nowrap"
                        onClick={handleFeatureRequest}
                        style={{
                            animation: isOpen ? 'slideInFromRight 0.3s ease-out 0.3s forwards' : 'none'
                        }}
                    >
                        <div className="p-2 bg-green-100 rounded-full">
                            <BulbIcon className="w-6 h-6 text-green-500" />
                        </div>
                        Feature Request
                    </button>
                </div>
            </div>
            
            {/* Main button */}
            <button 
                className={`rounded-full bg-gradient-to-r ${isOpen ? 'from-pink-500 to-red-500' : 'from-[#88C4FC] to-[#0085FF]'} p-3 sm:p-4 shadow-xl flex items-center justify-center transition-all duration-300`}
                onClick={() => setIsOpen(!isOpen)}
            >
                
                    <Image
                        src="/icons/feedback.svg"
                        width={28}
                        height={28}
                        alt="feedback"
                        className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10"
                    />
            </button>
            
            {/* Feedback Survey Modal */}
            {showSurvey && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-[fadeIn_0.3s_ease-out]">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold">
                                {feedbackType === 'bug' ? 'Report a Bug' : 'Request a Feature'}
                            </h3>
                            <button 
                                onClick={handleCloseSurvey}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <CustomIcon iconName="close" className="w-5 h-5" />
                            </button>
                        </div>
                        
                        {submitSuccess ? (
                            <div className="py-8 text-center">
                                <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                                    <CustomIcon iconName="check" className="w-6 h-6 text-green-500" />
                                </div>
                                <p className="text-lg font-medium text-gray-800">Thank you for your feedback!</p>
                                <p className="text-gray-500">We appreciate your input.</p>
                            </div>
                        ) : (
                            <>
                                <p className="text-gray-600 mb-4">
                                    {feedbackType === 'bug' 
                                        ? 'Please describe the issue you encountered in detail.' 
                                        : 'Please describe the feature you would like to see.'}
                                </p>
                                
                                {error && (
                                    <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                                        {error}
                                    </div>
                                )}
                                
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                            Your Name
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            placeholder="John Doe"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                            Your Email
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            placeholder="john@example.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                    </div>
                                </div>
                                
                                <div className="mb-4">
                                    <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-1">
                                        {feedbackType === 'bug' ? 'Describe the Bug' : 'Describe Your Feature Request'}
                                    </label>
                                    <textarea
                                        id="feedback"
                                        className="w-full h-32 border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                        placeholder={feedbackType === 'bug' 
                                            ? 'I found an issue when...' 
                                            : 'I would like to have a feature that...'}
                                        value={feedbackText}
                                        onChange={(e) => setFeedbackText(e.target.value)}
                                    ></textarea>
                                </div>
                                
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Attachment (Optional)
                                    </label>
                                    <div className="mt-1 flex items-center">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            accept="image/*,application/pdf,video/mp4"
                                            className="hidden"
                                            onChange={(e) => {
                                                if (e.target.files && e.target.files[0]) {
                                                    const file = e.target.files[0];
                                                    // Check file size (5MB = 5 * 1024 * 1024 bytes)
                                                    if (file.size > 5 * 1024 * 1024) {
                                                        setError('File size exceeds 5MB limit');
                                                        return;
                                                    }
                                                    setAttachment(file);
                                                    setError(null);
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                        >
                                            <CustomIcon iconName="upload" className="w-5 h-5 mr-2 text-gray-500" />
                                            {attachment ? `Change ${getFileTypeLabel(attachment.type)}` : 'Upload File'}
                                        </button>
                                        {attachment && (
                                            <div className="ml-3 flex items-center">
                                                <span className="text-sm text-gray-500 truncate max-w-[150px]">
                                                    {attachment.name}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => setAttachment(null)}
                                                    className="ml-2 text-gray-400 hover:text-gray-500"
                                                >
                                                    <CustomIcon iconName="close" className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">
                                        {feedbackType === 'bug' 
                                            ? 'Upload a screenshot or video of the issue (PNG, JPG, PDF, MP4 up to 5MB)' 
                                            : 'Upload an image, document or video to illustrate your feature request (PNG, JPG, PDF, MP4 up to 5MB)'}
                                    </p>
                                </div>
                                
                                <div className="flex justify-end">
                                    <button
                                        className="px-5 py-2 bg-gray-200 text-gray-800 rounded-lg mr-3 hover:bg-gray-300 transition-colors"
                                        onClick={handleCloseSurvey}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className={`px-5 py-2 bg-gradient-to-r from-[#88C4FC] to-[#0085FF] text-white rounded-lg hover:opacity-90 transition-colors flex items-center ${(!feedbackText.trim() || !name.trim() || !email.trim()) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        onClick={handleSubmitFeedback}
                                        disabled={!feedbackText.trim() || !name.trim() || !email.trim() || isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                                Submitting...
                                            </>
                                        ) : 'Submit Feedback'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}